import { useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Control } from "react-hook-form";

import type { Interview } from "@aglocal/schema/Interview";
import type { TalentMeetingDetails } from "@aglocal/schema/TalentMeetingDetails";

import { Span } from "@aglocal/web/components/Typography";

import getJobTitle from "@/helpers/getJobTitle";
import { UseFormSetDefault } from "@/hooks/useSetDefault";

import { PostMeetingFormWithSummary } from "../form";
import { FIELD_LABELS } from "../messages";

import StringField from "./StringField";

export interface TitleFieldProps {
  interview: Interview;
  details: Readonly<TalentMeetingDetails> | undefined;
  control: Control<PostMeetingFormWithSummary>;
  setDefault: UseFormSetDefault<PostMeetingFormWithSummary>;
}

export default function TitleField({
  interview,
  details,
  control,
  setDefault,
}: TitleFieldProps) {
  const { $t } = useIntl();
  const currentJobTitle = getJobTitle(interview);

  const defaultJobTitle = useMemo(() => {
    if (details?.jobTitle) return details.jobTitle;
    return currentJobTitle ?? "";
  }, [details?.jobTitle, currentJobTitle]);

  useEffect(() => {
    setDefault("jobTitle", defaultJobTitle);
  }, [defaultJobTitle, setDefault]);

  return (
    <StringField
      name="jobTitle"
      label={$t(FIELD_LABELS.jobTitle)}
      control={control}
      hint={
        currentJobTitle && (
          <FormattedMessage
            defaultMessage="Current Title: <b>{title}</b>"
            description="Hint under the job title field showing the title currently on the talent's Connected profile; {title} is that title."
            values={{
              title: currentJobTitle,
              b: (chunks) => (
                <Span size={200} weight="bold">
                  {chunks}
                </Span>
              ),
            }}
          />
        )
      }
    />
  );
}
